import * as React from 'react';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import {
  Box, InputAdornment, TextField, Typography,
} from '@mui/material';

import './styles.scss';

interface PrimaryTimePickerProps {
  value: any;
  onChange:(value: any) => void;
  minTime?:any;
  maxTime?:any;
  label?:string;
  disabled?:boolean;
}

const style = {
  border: 0,
  borderRadius: '6px',
  fontSize: '18px',
  fontWeight: 500,
  outline: 'none',
  marginTop: '5px',
  width: '100%',
  '& .MuiOutlinedInput-input': {
    padding: '11px 14px',
    textTransform: 'uppercase',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CDCDCD',
      padding: '11px 14px',
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CDCDCD',
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CDCDCD',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: '#171772',
      borderWidth: '1px',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#171772',
      borderWidth: '1px',
    },
    '& .MuiButtonBase-root': {
      color: '#CDCDCD',
    },
  },
};

const activeMeridiemStyles = { color: 'secondary.main' };

const PrimaryTimePicker = React.forwardRef(
  ({
    onChange, value, minTime, maxTime, label, disabled,
  }: PrimaryTimePickerProps, _ref) => {
    const isControlDisabled = Boolean(disabled);
    const [timeInput, setTimeInput] = React.useState(value ? value.format('hh:mm') : '');
    const [meridiem, setMeridiem] = React.useState(value?.format('A') || 'AM');

    // Handle manual input changes
    const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isControlDisabled) {
        return;
      }
      const newValue = e.target.value;
      setTimeInput(newValue);

      // Parse the input and call onChange if valid
      const parsedTime = moment(`${newValue} ${meridiem}`, 'hh:mm A', true);
      if (parsedTime.isValid()) {
        onChange(parsedTime);
      }
    };

    // Toggle AM/PM when clicking on it
    const toggleMeridiem = () => {
      if (isControlDisabled) {
        return;
      }
      const newMeridiem = meridiem === 'AM' ? 'PM' : 'AM';
      setMeridiem(newMeridiem);

      // Update the time with the new meridiem
      const updatedTime = moment(`${timeInput} ${newMeridiem}`, 'hh:mm A', true);
      if (updatedTime.isValid()) {
        onChange(updatedTime);
      }
    };

    // extract AM PM from the value
    React.useEffect(() => {
      if (value) {
        const newMeridiem = value.format('A');
        setMeridiem(newMeridiem);
      }
    }, [value]);

    return (
      <LocalizationProvider dateAdapter={AdapterMoment}>
        {label && <Typography variant="body1">{label}</Typography>}
        <MobileTimePicker
          value={value}
          ampmInClock
          disableIgnoringDatePartForTimeValidation
          inputFormat="hh:mm"
          minTime={minTime}
          maxTime={maxTime}
          onChange={(newValue) => {
            setTimeInput(newValue ? newValue.format('hh:mm') : '');
            setMeridiem(newValue ? newValue.format('A') : 'AM');
            onChange(newValue); // Update state when selecting from clock
          }}
          disabled={disabled}
          renderInput={(params) => {
            const meridiemOnClick = isControlDisabled ? undefined : toggleMeridiem;
            let amMeridiemStyles = meridiem === 'AM'
              ? activeMeridiemStyles
              : { color: 'text.secondary' };
            let pmMeridiemStyles = meridiem === 'PM'
              ? { ...activeMeridiemStyles, ml: 0.8 }
              : { color: 'text.secondary', ml: 0.8 };

            if (isControlDisabled) {
              amMeridiemStyles = { color: 'text.disabled' };
              pmMeridiemStyles = { color: 'text.disabled', ml: 0.8 };
            }

            return (
              <TextField
                sx={style}
                {...params}
                disabled={isControlDisabled}
                fullWidth
                value={timeInput}
                onChange={handleTimeInputChange} // Handle manual input
                InputProps={{
                  ...params.InputProps,
                  placeholder: 'hh:mm',
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: isControlDisabled ? 'default' : 'pointer',
                        }}
                      >
                        <Typography
                          variant="body1"
                          onClick={meridiemOnClick}
                          sx={amMeridiemStyles}
                        >
                          AM
                        </Typography>
                        <Typography
                          variant="body1"
                          onClick={meridiemOnClick}
                          sx={pmMeridiemStyles}
                        >
                          PM
                        </Typography>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            );
          }}
        />
      </LocalizationProvider>
    );
  },
);

PrimaryTimePicker.defaultProps = {
  minTime: '',
  maxTime: '',
  label: '',
  disabled: false,
};

export default PrimaryTimePicker;
